import { Dialog } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSwipeable } from "react-swipeable";
import {
  Alert,
  AlertAppID,
  AlertPriority,
  AlertStatus,
  AlertToken,
  fromAlertTokenToAppId,
  IAlertMonitor,
  Reminder,
} from "../../redux/alerts";
import {
  isFeedbackType,
  NotificationState,
  OneNotification,
} from "../../redux/newNotification/types";
import { ResidentActivityType } from "../../redux/residentActivityPlan";
import { presenceStatuses } from "../../redux/residents";
import { residentsCreator } from "../../redux/residents/selectors";
import { IAppState } from "../../redux/types";
import { Dispatcher } from "../../service/RootDispather";
import { alertIcon, alertIconToken, borderByToken } from "../../utils/AlertTo";
import { AvatarResidentUser } from "../common/AvatarUser";
import {
  DisplayUserInvisibleContainer,
  OverflowTip,
  RoomBedDisplayByMin,
  ViewerInvisibleContainer,
} from "../common/Common";
import ActivityFeedback from "../modals/ActivityFeedback";
import AlertFeedback from "../modals/AlertFeedback";
import VitalSign from "../modals/VitalSign";
import { TimePass } from "../status/OneNotificationView";
import { MarkForResidentAvatar } from "../status/utils";
import "./styles.scss";
const WAIT_UNTIL_CLOSE = 3000;

export const Notification = (): JSX.Element => {
  const rootDispatcher = Dispatcher();
  const { isLoggedOut } = useSelector((state: IAppState) => state.auth);
  const { residentServiceError, organizationServiceError } = useSelector(
    (state: IAppState) => state.healthCheck
  );
  const { notifications } = useSelector(
    (state: IAppState) => state.notification
  );
  const [notificationInWork, setnotificationInWork] = useState(
    notifications[notifications.length - 1]
  );
  useEffect(() => {
    setnotificationInWork(notifications[notifications.length - 1]);
  }, [notifications]);

  const onClose = useCallback(() => {
    rootDispatcher.clearNotification(notificationInWork);
  }, [notificationInWork, rootDispatcher]);
  const handlers = useSwipeable({
    onSwipedRight: () => onClose(),
  });
  if (
    isLoggedOut ||
    residentServiceError ||
    organizationServiceError ||
    notifications.length === 0
  )
    return null;
  return (
    <Dialog open={true} fullScreen={true} onClose={onClose}>
      <div className="notification-popup-view" {...handlers}>
        {notificationInWork?.state === NotificationState.new && (
          <NewContent notification={notificationInWork} />
        )}
        {notificationInWork?.state === NotificationState.view && (
          <ViewContent notification={notificationInWork} />
        )}
        {notificationInWork?.state === NotificationState.feedback && (
          <FeedbackContent notification={notificationInWork} />
        )}
        {notificationInWork?.state === NotificationState.confirm && (
          <ConfirmContent notification={notificationInWork} />
        )}
        {notificationInWork?.state === NotificationState.congrat && (
          <CongratsContent notification={notificationInWork} />
        )}
      </div>
    </Dialog>
  );
};

const CongratsContent = React.memo(
  ({ notification }: { notification: OneNotification }): JSX.Element => {
    const ResidentSelection = useMemo(() => residentsCreator(), []);
    const resident = useSelector((state: IAppState) =>
      ResidentSelection(state, notification?.residentId)
    );
    const rootDispatcher = Dispatcher();
    useEffect(() => {
      const timeout = setTimeout(
        () => rootDispatcher.clearNotification(notification),
        WAIT_UNTIL_CLOSE
      );
      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }, [notification, rootDispatcher]);

    return (
      <div className={`green border`}>
        <div className="notif-body ">
          <div className="flex-column flex-center flex-full-height">
            <div className="flex-gap">
              <AvatarResidentUser profile={resident} size={"24"}>
                <MarkForResidentAvatar resident={resident} />
              </AvatarResidentUser>
              <RoomBedDisplayByMin
                devicesMinimized={resident?.devicesMinimized || {}}
                isActive={resident?.nodePresence === presenceStatuses.active}
              />
            </div>
            <OverflowTip
              text={`${resident?.firstName} ${resident?.lastName}`}
              className="center"
            />
            <div className="alert-68" style={{ paddingTop: "8px" }}>
              {Object.values(AlertToken).indexOf(notification?.type as any) !==
              -1 ? (
                alertIcon(
                  fromAlertTokenToAppId(
                    notification.type as AlertToken
                  ) as AlertAppID
                )
              ) : (
                <i
                  className={`icon-alert info icon-action-${notification.type}`}
                />
              )}
              <div className="green-circle">
                <i className="icon-check" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
CongratsContent.displayName = "CongratsContent";
const ConfirmContent = React.memo(
  ({ notification }: { notification: OneNotification }): JSX.Element => {
    const rootDispatcher = Dispatcher();
    const { t } = useTranslation("common");
    const onCancelClick = useCallback(() => {
      rootDispatcher.clearNotification(notification);
    }, [notification, rootDispatcher]);
    const onDoneClickHandler = useCallback(() => {
      rootDispatcher.resolveAlert({
        alertToken: notification.type as AlertToken,
        residentId: notification.residentId,
        alert: {
          generated: notification.datetime,
          lastUpdate: notification.datetime,
          priority: 2,
          status: AlertStatus.done,
          feedback: null,
        },
      });
      rootDispatcher.clearNotification(notification);
    }, [notification, rootDispatcher]);

    return (
      <div className={`green border`}>
        <div className="notif-body congrats">
          {/* flex-column-8 */}
          <div className="flex-column flex-center" style={{ marginTop: "8px" }}>
            <div className="alert-32">
              {Object.values(AlertToken).indexOf(notification?.type as any) !==
              -1 ? (
                alertIcon(
                  fromAlertTokenToAppId(
                    notification.type as AlertToken
                  ) as AlertAppID
                )
              ) : (
                <i
                  className={`icon-alert info icon-action-${notification.type}`}
                />
              )}
            </div>
            <p>{t("labels.Confirm")}</p>
            <div className="flex-gap flex-gap-8">
              <button
                className="btn btn-icon secondary size-btn"
                onClick={onCancelClick}
                style={{ maxWidth: "none" }}
              >
                <i className="icon-close" />
              </button>
              <ViewerInvisibleContainer>
                <DisplayUserInvisibleContainer>
                  <button
                    className="btn primary btn-icon size-btn"
                    style={{ maxWidth: "none", marginLeft: "5px" }}
                    onClick={onDoneClickHandler}
                  >
                    <i className="icon-check"></i>
                  </button>
                </DisplayUserInvisibleContainer>
              </ViewerInvisibleContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
ConfirmContent.displayName = "ConfirmContent";
const NewContent = React.memo(
  ({ notification }: { notification: OneNotification }): JSX.Element => {
    const ResidentSelection = useMemo(() => residentsCreator(), []);
    const resident = useSelector((state: IAppState) =>
      ResidentSelection(state, notification?.residentId)
    );
    const rootDispatcher = Dispatcher();
    useEffect(() => {
      const timeout = setTimeout(
        () => rootDispatcher.clearNotification(notification),
        WAIT_UNTIL_CLOSE
      );
      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }, [notification, rootDispatcher]);
    const onClickView = useCallback(() => {
      rootDispatcher.notificationView({
        ...notification,
        state: NotificationState.view,
      });
    }, [notification, rootDispatcher]);

    return (
      <div
        className={`${borderByToken(notification.type)} border`}
        onClick={onClickView}
      >
        <div className="notif-body ">
          <div className="flex-column flex-center flex-gap-4 flex-full-height">
            <RoomBedDisplayByMin
              devicesMinimized={resident?.devicesMinimized || {}}
              isActive={resident?.nodePresence === presenceStatuses.active}
            />
            <OverflowTip
              text={`${resident?.firstName} ${resident?.lastName}`}
              className="center"
            />
            <AvatarResidentUser profile={resident} size={"68"}>
              <div className="status-holder-alert-bottom">
                <div className="alert-32" style={{ paddingTop: "8px" }}>
                  {Object.values(AlertToken).indexOf(
                    notification?.type as any
                  ) !== -1 ? (
                    alertIcon(
                      fromAlertTokenToAppId(
                        notification.type as AlertToken
                      ) as AlertAppID
                    )
                  ) : (
                    <i
                      className={`icon-alert info icon-action-${notification.type}`}
                    />
                  )}
                </div>
              </div>
            </AvatarResidentUser>
          </div>
        </div>
      </div>
    );
  }
);
NewContent.displayName = "NewContent";
const ViewContent = React.memo(
  ({ notification }: { notification: OneNotification }): JSX.Element => {
    const ResidentSelection = useMemo(() => residentsCreator(), []);
    const resident = useSelector((state: IAppState) =>
      ResidentSelection(state, notification?.residentId)
    );
    const rootDispatcher = Dispatcher();
    // useEffect(() => {
    // const timeout = setTimeout(
    //   () => rootDispatcher.clearNotification(notification),
    //   WAIT_UNTIL_CLOSE
    // );
    // return () => {
    //   if (timeout) clearTimeout(timeout);
    // };
    // }, [notification, rootDispatcher]);

    const onNextClickHandler = useCallback(() => {
      rootDispatcher.notificationView({
        ...notification,
        state: isFeedbackType(notification.type)
          ? NotificationState.feedback
          : NotificationState.confirm,
      });
    }, [rootDispatcher, notification]);
    const isReminder =
      Object.values(AlertToken).indexOf(notification.type as AlertToken) === -1;
    const reminder = isReminder ? notification : undefined;
    const al = !isReminder ? notification : undefined;

    return (
      <div className={` border`}>
        <div className="notif-body clear">
          <div
            // flex-column-16
            className={`one-notif-card flex-column ${borderByToken(
              notification.type
            )} flex-full-height`}
          >
            <div className="flex-column">
              {/* flex-gap-8 */}
              <div
                className="flex-gap flex-center"
                style={{ marginTop: "8px" }}
              >
                <AvatarResidentUser profile={resident} size={"68"}>
                  <MarkForResidentAvatar resident={resident} />
                </AvatarResidentUser>
                <RoomBedDisplayByMin
                  devicesMinimized={resident.devicesMinimized}
                  isActive={resident.nodePresence === presenceStatuses.active}
                />
              </div>
              <OverflowTip
                text={[resident?.firstName, resident.lastName].join(" ")}
              />
            </div>
            <div className="flex-column flex-center flex-column-8">
              {/* flex-gap-8" */}
              <div
                className="flex-gap flex-center"
                style={{ marginTop: "8px" }}
              >
                {al && (
                  <div className="alert-44">
                    {alertIconToken(notification.type as AlertToken)}
                  </div>
                )}
                {reminder && (
                  <>
                    <div className="alert-44">
                      <i
                        className={`icon-alert info icon-action-${notification.type}`}
                      />
                    </div>
                  </>
                )}
                {notification.type !== AlertToken.UserNearby && (
                  <ViewerInvisibleContainer>
                    <DisplayUserInvisibleContainer>
                      <button
                        className="btn primary btn-icon size-btn"
                        onClick={onNextClickHandler}
                        style={{ maxWidth: "none", marginLeft: "10px" }}
                      >
                        <i className="icon-check"></i>
                      </button>
                    </DisplayUserInvisibleContainer>
                  </ViewerInvisibleContainer>
                )}
              </div>
              <TimePass
                alert={
                  {
                    ...notification,
                    date: notification.datetime,
                  } as unknown as IAlertMonitor
                }
                onlyMinutes={false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);
ViewContent.displayName = "ViewContent";
const FeedbackContent = React.memo(
  ({ notification }: { notification: OneNotification }): JSX.Element => {
    const rootDispatcher = Dispatcher();
    const isReminder =
      Object.values(AlertToken).indexOf(notification.type as AlertToken) === -1;
    const innerCallBack = useCallback(() => {
      rootDispatcher.clearNotification(notification);
    }, [rootDispatcher, notification]);

    const reminder: Reminder = {
      activityType: notification?.type as ResidentActivityType,
      residentId: notification?.residentId,
      lastUpdate: notification?.datetime,
      nodeId: -1,
      utcDate: notification?.datetime,
      activityParams: notification.activityParams,
    };
    const alert: Alert = {
      alertToken: notification?.type as AlertToken,
      patientId: notification.residentId,
      patientName: "",
      date: notification.datetime,
      lastUpdate: notification.datetime,
      priority: AlertPriority.high,
      status: AlertStatus.open,
    };
    return (
      <div className={` border`}>
        <div className="notif-body clear">
          {isReminder ? (
            <>
              {notification.type === ResidentActivityType.VITAL_SIGNS ? (
                <VitalSign
                  callback={innerCallBack}
                  reminder={reminder}
                  residentId={notification.residentId}
                />
              ) : (
                <ActivityFeedback
                  reminder={reminder}
                  residentId={notification.residentId}
                  callback={innerCallBack}
                />
              )}
            </>
          ) : (
            <AlertFeedback alert={alert} callback={innerCallBack} />
          )}
        </div>
      </div>
    );
  }
);
FeedbackContent.displayName = "FeedbackContent";
export default React.memo(Notification);
